import { useEffect, useRef } from "react";

const VERT = `
attribute vec2 aPos;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform vec2 uMouse;
uniform float uTime;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.04;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float aspect = uRes.x / max(uRes.y, 1.0);
  vec2 p = uv * vec2(aspect, 1.0);
  vec2 mouse = (uMouse - 0.5) * 0.42;
  float t = uTime * 0.055;
  float mist = fbm(p * 1.28 + vec2(t, -t * 0.35) + mouse);
  float vein = fbm(p * 2.35 - vec2(t * 0.55, t * 0.8) - mouse * 0.6);
  float mistMask = smoothstep(0.36, 0.78, mist);
  float falconMask = smoothstep(0.62, 0.9, vein) * smoothstep(0.42, 0.7, mist);
  vec3 charcoal = vec3(0.055, 0.055, 0.06);
  vec3 falcon = vec3(0.890, 0.110, 0.145);
  vec3 col = mix(charcoal, falcon, falconMask * 0.9);
  float alpha = mistMask * 0.26 + falconMask * 0.42;
  gl_FragColor = vec4(col, alpha);
}
`;

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function startEther(wrap: HTMLDivElement, canvas: HTMLCanvasElement): (() => void) | undefined {
  const gl = canvas.getContext("webgl", {
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: false,
    powerPreference: "low-power",
  });
  if (!gl) return;

  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) return;

  const program = gl.createProgram();
  if (!program) return;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
  gl.useProgram(program);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const uRes = gl.getUniformLocation(program, "uRes");
  const uMouse = gl.getUniformLocation(program, "uMouse");
  const uTime = gl.getUniformLocation(program, "uTime");

  let running = true;
  let visible = true;
  let raf = 0;
  let w = 0;
  let h = 0;
  const mouse = { x: 0.62, y: 0.42, tx: 0.62, ty: 0.42 };
  const t0 = performance.now();

  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
    const nextW = Math.max(1, Math.floor(wrap.clientWidth * dpr));
    const nextH = Math.max(1, Math.floor(wrap.clientHeight * dpr));
    if (nextW === w && nextH === h) return;
    w = nextW;
    h = nextH;
    canvas.width = w;
    canvas.height = h;
    gl.viewport(0, 0, w, h);
  };

  const onMove = (event: MouseEvent) => {
    const rect = wrap.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    mouse.tx = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    mouse.ty = Math.min(1, Math.max(0, 1 - (event.clientY - rect.top) / rect.height));
  };

  const draw = (now: number) => {
    if (!running) return;
    if (visible && !document.hidden) {
      mouse.x += (mouse.tx - mouse.x) * 0.045;
      mouse.y += (mouse.ty - mouse.y) * 0.045;
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(uRes, w, h);
      gl.uniform2f(uMouse, mouse.x, mouse.y);
      gl.uniform1f(uTime, (now - t0) / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    raf = requestAnimationFrame(draw);
  };

  const io = new IntersectionObserver(
    ([entry]) => {
      visible = Boolean(entry?.isIntersecting);
    },
    { threshold: 0.05 },
  );
  io.observe(wrap);
  const ro = new ResizeObserver(resize);
  ro.observe(wrap);
  window.addEventListener("mousemove", onMove, { passive: true });
  resize();
  raf = requestAnimationFrame(draw);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    io.disconnect();
    ro.disconnect();
    window.removeEventListener("mousemove", onMove);
    gl.deleteBuffer(buf);
    gl.deleteProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
  };
}

/**
 * Sparse charcoal + Falcon liquid overlay (React Bits Liquid Ether pattern).
 * Accent only — the LCP launch still stays an `<img>` underneath.
 */
export function LiquidEther() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    let stop: (() => void) | undefined;

    const sync = () => {
      stop?.();
      stop = undefined;
      if (!mq.matches) stop = startEther(wrap, canvas);
    };

    sync();
    mq.addEventListener("change", sync);
    return () => {
      mq.removeEventListener("change", sync);
      stop?.();
    };
  }, []);

  return (
    <div ref={wrapRef} className="mission-ether pointer-events-none absolute inset-0 z-[1]" aria-hidden>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
